import React from "react"
import { Tag } from "@fider/models"
import { Checkbox, FilterSidebar, FilterSidebarItem, FilterSidebarSectionTitle } from "@fider/components"
import { HStack } from "@fider/components/layout"
import { useFider } from "@fider/hooks"
import { i18n } from "@lingui/core"
import { FilterState } from "./PostsContainer"

type FilterType = "status" | "myVotes" | "myPosts"

interface OptionItem {
  value: string | boolean
  label: string
  count?: number
  type: FilterType
}

interface PostFilterProps {
  activeFilter: FilterState
  countPerStatus: { [key: string]: number }
  filtersChanged: (filter: FilterState) => void
  tags: Tag[]
}

export interface FilterItem {
  type: FilterType
  value: string | boolean
}

const FilterStateToFilterItems = (filterState: FilterState): FilterItem[] => {
  const filterItems: FilterItem[] = []
  filterState.statuses.forEach((s) => {
    filterItems.push({ type: "status", value: s })
  })
  if (filterState.myVotes) {
    filterItems.push({ type: "myVotes", value: true })
  }
  if (filterState.myPosts) {
    filterItems.push({ type: "myPosts", value: true })
  }
  return filterItems
}

const FilterItemsToFilterState = (filterItems: FilterItem[], tags: string[], noTags: boolean): FilterState => {
  const filterState: FilterState = { tags, statuses: [], myVotes: false, noTags, myPosts: false }
  filterItems.forEach((i) => {
    if (i.type === "status") {
      filterState.statuses.push(i.value as string)
    } else if (i.type === "myVotes") {
      filterState.myVotes = true
    } else if (i.type === "myPosts") {
      filterState.myPosts = true
    }
  })
  return filterState
}

export const PostFilter = (props: PostFilterProps) => {
  const fider = useFider()

  const filterItems: FilterItem[] = FilterStateToFilterItems(props.activeFilter)

  const handleChangeFilter = (item: OptionItem) => () => {
    const exists = filterItems.find((i) => i.type === item.type && i.value === item.value)
    const newFilter = exists
      ? filterItems.filter((i) => !(i.type === item.type && i.value === item.value))
      : [...filterItems, { type: item.type, value: item.value }]

    props.filtersChanged(FilterItemsToFilterState(newFilter, props.activeFilter.tags, props.activeFilter.noTags))
  }

  // The product switcher is single-select: picking a product replaces
  // whatever tag/noTags selection was active, rather than combining them.
  const selectProduct = (tag: string | null, noTags: boolean) => () => {
    props.filtersChanged(FilterItemsToFilterState(filterItems, tag ? [tag] : [], noTags))
  }

  const options: OptionItem[] = []

  if (fider.session.isAuthenticated) {
    options.push({ value: true, label: i18n._({ id: "home.postfilter.option.myvotes", message: "My Votes" }), type: "myVotes" })
    options.push({ value: true, label: i18n._({ id: "home.postfilter.option.myposts", message: "My Posts" }), type: "myPosts" })
  }

  fider.session.postStatuses.forEach((s) => {
    options.push({
      label: s.name,
      value: s.slug,
      count: props.countPerStatus[s.slug],
      type: "status",
    })
  })

  // Add Pending status for collaborators and admins
  if (fider.session.isAuthenticated && fider.session.user.isCollaborator) {
    options.push({
      label: i18n._({ id: "home.postfilter.option.pending", message: "Pending" }),
      value: "pending",
      type: "status",
    })
  }

  const FilterGroupSection = ({ title, type }: { title: string; type: string[] }) => {
    const sectionOptions = options.filter((o) => type.includes(o.type))

    if (sectionOptions.length === 0) return null

    return (
      <>
        <FilterSidebarSectionTitle>{title}</FilterSidebarSectionTitle>

        {sectionOptions.map((o) => {
          const isChecked = filterItems.some((f) => f.type === o.type && f.value === o.value)
          const fieldKey = `${o.type}:${o.value.toString()}`

          return (
            <div className="c-filter-sidebar__item" key={fieldKey} onClick={handleChangeFilter(o)}>
              <Checkbox field={fieldKey} checked={isChecked}>
                <HStack spacing={2}>
                  <span className={isChecked ? "text-semibold" : ""}>{o.label}</span>
                  {o.count && o.count > 0 && <span className="bg-gray-200 inline-block rounded-full px-1 w-min-4 text-2xs text-center">{o.count}</span>}
                </HStack>
              </Checkbox>
            </div>
          )
        })}
      </>
    )
  }

  const noProductSelected = props.activeFilter.tags.length === 0 && !props.activeFilter.noTags

  return (
    <FilterSidebar title={i18n._({ id: "label.tags", message: "Tags" })}>
      <FilterSidebarItem
        label={i18n._({ id: "home.postfilter.option.all", message: "All" })}
        isActive={noProductSelected}
        onClick={selectProduct(null, false)}
      />
      {props.tags.map((tag) => (
        <FilterSidebarItem
          key={tag.id}
          label={tag.name}
          color={tag.color}
          isActive={props.activeFilter.tags.indexOf(tag.slug) >= 0}
          onClick={selectProduct(tag.slug, false)}
        />
      ))}
      {props.tags.length > 0 && (
        <FilterSidebarItem
          label={i18n._({ id: "home.postfilter.option.notags", message: "Untagged" })}
          isActive={props.activeFilter.noTags}
          onClick={selectProduct(null, true)}
        />
      )}

      <FilterGroupSection title={i18n._({ id: "home.postfilter.label.myactivity", message: "My activity" })} type={["myVotes", "myPosts"]} />
      <FilterGroupSection title={i18n._({ id: "home.postfilter.label.status", message: "Status" })} type={["status"]} />
    </FilterSidebar>
  )
}
